import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '@/common/SafeIcon';

const options = [
  { value: 1, label: 'Never' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Consistently' }
];

export default function QuizSection({ section, index, totalSections, answers, setAnswers, onNext }) {
  
  const handleSelect = (qId, val) => {
    setAnswers(prev => ({ ...prev, [qId]: val }));
  };

  const isComplete = section.questions.every(q => answers[q.id]);
  const progressPercentage = ((index) / totalSections) * 100;

  return (
    <motion.div 
      key={section.id}
      initial={{ opacity: 0, x: 20 }} 
      animate={{ opacity: 1, x: 0 }} 
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="max-w-3xl mx-auto w-full"
    >
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm font-heading text-brand-blue/60 uppercase tracking-widest font-bold">
            Section {index + 1} of {totalSections}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-2 w-full bg-brand-blue/10 rounded-full overflow-hidden mb-8">
          <motion.div 
            initial={{ width: `${((index - 1) / totalSections) * 100}%` }}
            animate={{ width: `${(index / totalSections) * 100}%` }}
            className="h-full bg-brand-blue rounded-full"
          />
        </div>

        <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-blue mb-4 leading-tight">
          {section.label} <span className="text-brand-blue/30">&middot;</span> {section.title}
        </h2>
        <p className="text-xl text-brand-orange font-body font-medium">{section.desc}</p>
      </div>

      <div className="space-y-6">
        {section.questions.map((q, idx) => (
          <div key={q.id} className="bg-white p-6 md:p-8 rounded-[2rem] shadow-lg shadow-brand-blue/5 border border-brand-blue/5 hover:border-brand-blue/20 transition-colors">
            <p className="font-heading font-semibold text-lg md:text-xl text-gray-800 mb-6 leading-relaxed">
              <span className="text-brand-blue/40 mr-2">{idx + 1}.</span> {q.text}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {options.map(opt => {
                const isSelected = answers[q.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(q.id, opt.value)}
                    className={`py-4 px-2 rounded-xl text-sm md:text-base font-body font-bold transition-all duration-200 border-2 ${
                      isSelected 
                        ? 'bg-brand-blue border-brand-blue text-white shadow-md transform scale-[1.02]' 
                        : 'bg-transparent border-brand-beige/50 text-brand-blue hover:bg-brand-beige/30 hover:border-brand-blue/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex justify-end">
        <button 
          onClick={onNext}
          disabled={!isComplete}
          className={`font-heading font-bold py-4 px-10 rounded-full transition-all duration-300 flex items-center space-x-2 ${
            isComplete 
              ? 'bg-brand-orange text-white hover:bg-[#E56A42] shadow-lg shadow-brand-orange/30 hover:-translate-y-1' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <span>{index === totalSections - 1 ? 'See Results' : 'Next Section'}</span>
          <SafeIcon name="ArrowRight" className={isComplete ? "text-white" : "text-gray-400"} />
        </button>
      </div>
    </motion.div>
  )
}